import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { finalize, map, mergeMap, startWith, switchMap } from 'rxjs/operators';

import { CropSettingsDialogComponent } from '../../components/cropsetting-dialog/cropsetting-dialog.component';
import { CropSettingsState } from '../../cropsetting.model';
import { CropSettingsService } from '../../services/cropsetting.service';
import * as CropSettingActions from '../actions/cropsetting.actions';

@Injectable()
export class CropSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(CropSettingsService);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly store = inject<Store<CropSettingsState>>(Store);

  loadCropSettingsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropSettingActions.loadCropSettingsRequest),
      mergeMap(() =>
        this.api
          .getCropSettings()
          .pipe(
            map((cropSettings) => CropSettingActions.loadCropSettingsSuccess({ cropSettings })),
          ),
      ),
    ),
  );

  addCropSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropSettingActions.addCropSetting),
      map((_) =>
        CropSettingActions.editCropSetting({
          cropSetting: {
            id: 0,
            cropId: null,
            cropName: '',
            cultivationMonthsFrom: null,
            cultivationMonthsUntil: null,
            norm: null,
            calendarYear: null,
          },
        }),
      ),
    ),
  );

  editCropSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropSettingActions.editCropSetting),
      mergeMap((action) =>
        this.dialog
          .form(CropSettingsDialogComponent, {
            cropSetting: {
              ...action.cropSetting,
            },
            title: action.cropSetting.cropId
              ? this.locale.translate('editCropSetting')
              : this.locale.translate('addCropSetting'),
          })
          .pipe(
            switchMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? of(
                    CropSettingActions.upsertCropSetting({
                      cropSetting: { id: action.cropSetting.id, ...result },
                    }),
                  )
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  upsertCropSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropSettingActions.upsertCropSetting),
      mergeMap((action) =>
        this.api.upsertCropSetting(action.cropSetting).pipe(
          switchMap(() => [CropSettingActions.loadCropSettingsRequest()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteCropSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropSettingActions.deleteCropSetting),
      mergeMap((action) =>
        this.api.deleteCropSetting(action.cropSetting).pipe(
          switchMap(() => [CropSettingActions.loadCropSettingsRequest()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
